import React, {FC, ReactNode, useCallback, useEffect, useReducer, useRef} from 'react';
import {instance} from '../../../services/core';
import {authenticationReducer} from './authentication-reducer';
import {
  AuthenticationContext,
  authenticationInitialState,
} from './authentication-context';
import {api} from "../../../services";
import {AuthResponse} from '../../../services/auth/authModel';
import {toast} from 'react-hot-toast';

interface AuthenticationContextProps {
  children: ReactNode;
}

const TWO_MINUTES_IN_MS = 120000;
const refreshDelay = ({token, expiresAt}: AuthResponse): number => {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (!token || !Number.isFinite(remaining) || remaining <= 0) {
    throw new Error('The session has expired. Please sign in again.');
  }
  // Short-lived tokens must not trigger an immediate refresh loop.
  return Math.min(2147483647, Math.max(1000,
    remaining > TWO_MINUTES_IN_MS ? remaining - TWO_MINUTES_IN_MS : remaining / 2));
};

const reportFailure = (error: unknown) => {
  const failure = error as {status?: number; message?: string};
  if (failure?.status !== 401) {
    toast.error(failure?.message || 'Unable to restore your session. Please try again.');
  }
};

export const AuthenticationProvider: FC<AuthenticationContextProps> = ({
                                                                         children,
                                                                       }) => {
  const [authentication, dispatchAuthentication] = useReducer(
    authenticationReducer,
    authenticationInitialState
  );

  const mounted = useRef(false);
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cookieMutation = useRef<Promise<void>>(Promise.resolve());

  const changeCookie = useCallback(function serialize<T>(request: () => Promise<T>): Promise<T> {
    // Browsers apply Set-Cookie even when a stale response is ignored by React.
    const result = cookieMutation.current.then(request);
    cookieMutation.current = result.then(() => undefined, () => undefined);
    return result;
  }, []);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const setBearer = useCallback((token?: string) => {
    const headers = instance.defaults.headers;
    // Remove legacy root/common/method defaults without replacing Axios's headers.
    [headers, headers.common, headers.get, headers.post, headers.put,
      headers.patch, headers.delete, headers.head, headers.options].forEach(group => {
      if (group) {
        Object.keys(group).forEach(name => {
          if (name.toLowerCase() === 'authorization') delete group[name];
        });
      }
    });
    if (token) headers.Authorization = `Bearer ${token}`;
  }, []);

  const invalidate = useCallback(() => {
    generation.current += 1;
    clearTimer();
    setBearer();
    return generation.current;
  }, [clearTimer, setBearer]);

  const isCurrent = useCallback((operation: number) =>
    mounted.current && generation.current === operation, []);

  const clearSession = useCallback(() => {
    invalidate();
    dispatchAuthentication({
      type: 'INITIALIZE',
      payload: {isAuthenticated: false, user: null},
    });
  }, [invalidate]);

  const scheduleRefresh = useCallback(function schedule(session: AuthResponse, operation: number) {
    const delay = refreshDelay(session);
    clearTimer();
    timer.current = setTimeout(() => {
      timer.current = null;
      const refresh = async () => {
        if (!isCurrent(operation)) return;
        try {
          const nextSession = await api.auth.refreshToken();
          if (!isCurrent(operation)) return;
          refreshDelay(nextSession);
          setBearer(nextSession.token);
          dispatchAuthentication({
            type: 'REFRESH',
            payload: {token: nextSession.token, expiresAt: new Date(nextSession.expiresAt)},
          });
          schedule(nextSession, operation);
        } catch (error) {
          if (!isCurrent(operation)) return;
          clearSession();
          reportFailure(error);
        }
      };
      void refresh();
    }, delay);
  }, [clearTimer, clearSession, isCurrent, setBearer]);

  const authenticate = useCallback(async (
    request: () => Promise<AuthResponse>,
    initializing = false
  ): Promise<void> => {
    if (!mounted.current) return;
    const operation = invalidate();
    try {
      const session = await request();
      if (!isCurrent(operation)) return;
      refreshDelay(session);
      setBearer(session.token);
      const user = await api.auth.me();
      if (!isCurrent(operation)) return;
      scheduleRefresh(session, operation);
      dispatchAuthentication({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: true,
          user,
          token: session.token,
          expiresAt: new Date(session.expiresAt),
        },
      });
    } catch (error) {
      if (!isCurrent(operation)) return;
      clearSession();
      if (initializing) reportFailure(error);
      else throw error;
    }
  }, [invalidate, isCurrent, scheduleRefresh, setBearer, clearSession]);

  useEffect(() => {
    mounted.current = true;
    void authenticate(api.auth.refreshToken, true);
    return () => {
      mounted.current = false;
      invalidate();
    };
  }, [authenticate, invalidate]);

  const login = useCallback(
    (username: string, password: string): Promise<void> => {
      return authenticate(() => changeCookie(() => api.auth.login({username, password})));
    },
    [authenticate, changeCookie]
  );

  const loginWithGoogle = useCallback(
    async (credential?: string): Promise<void> => {
      if (!credential) {
        throw new Error('No credential provided');
      }

      return authenticate(() => changeCookie(() => api.auth.loginWithGoogle(credential)));
    },
    [authenticate, changeCookie]
  );

  const logout = useCallback(async (): Promise<void> => {
    if (!mounted.current) return;
    clearSession();
    await changeCookie(api.auth.logout);
  }, [clearSession, changeCookie]);

  return (
    <AuthenticationContext.Provider
      value={{
        ...authentication,
        platform: 'JWT',
        login,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
